import React from 'react'
import { useTranslation } from 'react-i18next'
import { PlaylistItem } from '../../swagger'
import { useSortable } from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import { Box, Card, CardContent, Typography, useTheme } from '@mui/material'
import MusicNoteIcon from '@mui/icons-material/MusicNote'
import PlayArrowIcon from '@mui/icons-material/PlayArrow'
import { GridDeleteIcon } from '@mui/x-data-grid'
import DragIndicatorIcon from '@mui/icons-material/DragIndicator'
import RowActions, { Action } from '../Table/RowActions'

export interface SortableItemProps {
  item: PlaylistItem
  onClick: () => void
  onDeleteClick: () => void
  isShared?: boolean
}

interface PlaylistItemRow {
  handleClick: () => void
}

/**
 * SortableItem component representing a draggable playlist item
 * @param item - playlist item to render
 */
const SortableItem: React.FC<SortableItemProps> = ({
  item,
  isShared,
  onClick,
  onDeleteClick,
}) => {
  const theme = useTheme()
  const { t } = useTranslation()

  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: item.playlistItemKey })

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  }

  const playlistItemActions: Action<PlaylistItemRow>[] = [
    {
      actionName: t('SortableItem.PlaylistItemsMenu.Action.Delete', 'Delete'),
      actionKey: 'deletePlaylistItem',
      actionFunction: (row) => row.handleClick(),
      menuItemIcon: <GridDeleteIcon />,
      hide: () => !!isShared,
    },
  ]

  return (
    <Card
      ref={setNodeRef}
      sx={{
        flex: 1,
        position: 'relative',
        ...style,
        boxShadow: isDragging ? 3 : 1,
        touchAction: isDragging ? 'none' : 'auto',
      }}
      {...attributes}
      /** Let's leave this only for testing purposes. */
      {...(process.env.NODE_ENV === 'test' ? { ...listeners } : {})}
    >
      <CardContent
        tabIndex={process.env.NODE_ENV === 'test' ? 0 : -1}
        sx={{
          height: '100%',
          display: 'flex',
          alignItems: 'center',
          cursor: 'pointer',
        }}
        onClick={onClick}
        data-testid={`playlist-item-${item.playlistItemKey}`}
      >
        <Box
          {...listeners}
          sx={{
            cursor: 'grab',
            display: 'flex',
            alignItems: 'center',
            mr: 1,
          }}
        >
          {!isShared && <DragIndicatorIcon />}
        </Box>
        <Box p={0}>
          {item.asset?.filetype === 'Audio' ? (
            <MusicNoteIcon sx={{ fontSize: theme.spacing(5) }} />
          ) : (
            <PlayArrowIcon sx={{ fontSize: theme.spacing(5) }} />
          )}
        </Box>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            minWidth: 0,
          }}
        >
          <Typography
            sx={{
              textTransform: 'uppercase',
              marginInlineStart: theme.spacing(6),
              [theme.breakpoints.down(500)]: {
                flex: 1,
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              },
            }}
            variant="subtitle1"
          >
            {t('SortableItem.Card.Title', '{{title}}', {
              title: item.asset?.title,
            })}
          </Typography>
        </Box>
        <Box ml={'auto'} onClick={(e) => e.stopPropagation()}>
          <RowActions
            elementsCount={playlistItemActions.length}
            actions={playlistItemActions}
            row={{ handleClick: onDeleteClick }}
            rowId={item.playlistItemKey}
          />
        </Box>
      </CardContent>
    </Card>
  )
}

export default SortableItem
